import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { selectUser } from "../../components/auth/auth.selector";
import { logout } from "../../redux/reducers/authSlice";
import { BodyBuilder, WTFNewLogo } from "../../exports/Images";
import useIsMobile from "../../hooks/isMobile";
import { sidebarSelector, toggleSideBar } from "../../redux/reducers/sidebar";
import { fetchUserMappedFeatureDataV1 } from "../../redux/dashboard-features/featuresSlice";
import ProfileDropdown from "../dropdown/ProfileDropdown";
import GymSwitcher from "./GymSwitcher";
import { SidebarMenuSkeleton } from "../skeleton/SkeletonLoader";
import {
  getMenuIcon,
  ChevronDownIcon,
  HamburgerIcon,
  SettingsIcon,
  LogoutIcon,
} from "../icons/SidebarIcons";

const SideBarContentV2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const userInfoData = useSelector(selectUser);
  const isOpen = useSelector(sidebarSelector);
  const isMobile = useIsMobile();

  const {
    userMappedFeaturesV1,
    userMappedFeaturesV1Loading,
    userMappedFeaturesV1Error,
  } = useSelector((state) => state.features);

  const sidebarRef = useRef(null);
  const profileRef = useRef(null);
  const popoverRef = useRef(null);
  const isProfileClickRef = useRef(false);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [allMenus, setAllMenus] = useState([]);
  const [isProfilePopoverOpen, setIsProfilePopoverOpen] = useState(false);

  const handleToggleMenu = (id) => {
    // If sidebar is collapsed, expand it first
    if (!isOpen) {
      dispatch(toggleSideBar(true));
      // Set the submenu to open after expanding
      setTimeout(() => {
        setOpenSubMenus((prev) => ({
          ...prev,
          [id]: true,
        }));
      }, 100);
    } else {
      setOpenSubMenus((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    }
  };

  const handleMenuClick = (e) => {
    // If sidebar is collapsed, expand it
    if (!isOpen) {
      e.preventDefault();
      dispatch(toggleSideBar(true));
    } else if (isMobile) {
      dispatch(toggleSideBar(false));
    }
  };

  const handleSubMenuClick = () => {
    if (isMobile) {
      dispatch(toggleSideBar(false));
    }
  };

  function getAllMappedFeatures(filters) {
    dispatch(
      fetchUserMappedFeatureDataV1({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  function handleLogout() {
    navigate("/login", { state: { from: pathname } });
    dispatch(logout());
  }

  const handleToggle = useCallback(() => {
    try {
      dispatch(toggleSideBar(!isOpen));
    } catch (error) {
      console.log(error);
    }
  }, [isOpen, dispatch]);

  const isActiveRoute = (path) => {
    return pathname === path || pathname.startsWith(path + "/");
  };

  const isMenuActive = (menuGroup) => {
    if (menuGroup.path && isActiveRoute(menuGroup.path)) return true;
    if (menuGroup.sub_menu?.length > 0) {
      return menuGroup.sub_menu.some((item) => isActiveRoute(item.path));
    }
    return false;
  };

  const handleProfileMouseDown = (e) => {
    // Set flag on mousedown (fires before the document mousedown listener)
    e.stopPropagation();
    isProfileClickRef.current = true;
  };

  const handleProfileClick = () => {
    setIsProfilePopoverOpen((prev) => !prev);
    // Reset the flag after state update
    setTimeout(() => {
      isProfileClickRef.current = false;
    }, 0);
  };

  const closePopover = () => {
    setIsProfilePopoverOpen(false);
  };

  useEffect(() => {
    if (userInfoData?.uid || userInfoData?.user_id) {
      const filters = {
        user_id: userInfoData?.uid || userInfoData?.user_id || "",
      };
      getAllMappedFeatures(filters);
    }
  }, []);

  useEffect(() => {
    if (
      userMappedFeaturesV1?.status &&
      userMappedFeaturesV1?.data?.length > 0 &&
      !userMappedFeaturesV1Loading &&
      !userMappedFeaturesV1Error
    ) {
      const uniqueMenus = userMappedFeaturesV1.data.reduce((acc, menu) => {
        if (!acc.find((m) => m.menu_id === menu.menu_id)) {
          const uniqueSubMenus = menu.sub_menu?.reduce((subAcc, subItem) => {
            if (!subAcc.find((s) => s.feature_id === subItem.feature_id)) {
              subAcc.push(subItem);
            }
            return subAcc;
          }, []);
          acc.push({
            ...menu,
            sub_menu: uniqueSubMenus || [],
          });
        }
        return acc;
      }, []);
      setAllMenus(uniqueMenus);
    } else {
      setAllMenus([]);
    }
  }, [
    userMappedFeaturesV1,
    userMappedFeaturesV1Loading,
    userMappedFeaturesV1Error,
  ]);

  useEffect(() => {
    const initialOpenState = allMenus.reduce((acc, menu) => {
      const hasActiveChild = menu.sub_menu?.some((item) =>
        isActiveRoute(item.path)
      );
      acc[menu.menu_id] = hasActiveChild;
      return acc;
    }, {});
    setOpenSubMenus(initialOpenState);
  }, [allMenus, pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileClickRef.current) {
        return;
      }

      const clickedOnProfile = profileRef.current?.contains(event.target);
      const clickedOnPopover = popoverRef.current?.contains(event.target);
      const clickedInsideSidebar = sidebarRef.current?.contains(event.target);

      if (isProfilePopoverOpen && !clickedOnProfile && !clickedOnPopover) {
        setIsProfilePopoverOpen(false);
      }

      if (isOpen && !clickedInsideSidebar && !clickedOnPopover) {
        handleToggle();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleToggle, isProfilePopoverOpen]);

  const sidebarClass = `v2-sidebar ${isOpen ? "v2-sidebar--expanded" : "v2-sidebar--collapsed"}`;

  return (
    <>
      {isMobile && isOpen && (
        <div
          className={`v2-sidebar__overlay ${isOpen ? "v2-sidebar__overlay--visible" : ""}`}
          onClick={handleToggle}
        />
      )}

      <aside className={sidebarClass} ref={sidebarRef}>
        <div className="v2-sidebar__header">
          {isOpen && (
            <div className="v2-sidebar__logo-section">
              <img src={WTFNewLogo} alt="WTF" className="v2-sidebar__logo" />
              <div className="v2-sidebar__brand">
                <span className="v2-sidebar__brand-name">WTF GYMS</span>
                <span className="v2-sidebar__brand-tagline">Fitness Simplified</span>
              </div>
            </div>
          )}
          <button className="v2-sidebar__toggle" onClick={handleToggle}>
            <HamburgerIcon />
          </button>
        </div>

        <div className="v2-popover-trigger">
          <div
            ref={profileRef}
            className="v2-sidebar__profile"
            onMouseDown={handleProfileMouseDown}
            onClick={handleProfileClick}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleProfileClick();
              }
            }}
          >
            <img
              src={userInfoData?.profile || BodyBuilder}
              alt="Profile"
              className="v2-sidebar__profile-avatar"
            />
            {isOpen && (
              <div className="v2-sidebar__profile-info">
                <span className="v2-sidebar__profile-name">
                  {userInfoData?.name || "Anonymous"}
                </span>
                <span className="v2-sidebar__profile-role">
                  {userInfoData?.is_club_manager
                    ? "Club Manager"
                    : userInfoData?.account_type?.replace(/_/g, " ") || "User"}
                </span>
              </div>
            )}
          </div>
          {isOpen && <GymSwitcher />}
          <ProfileDropdown
            isOpen={isProfilePopoverOpen}
            onClose={closePopover}
          />
        </div>

        <nav className="v2-sidebar__nav">
          {userMappedFeaturesV1Loading ? (
            <ul className="v2-sidebar__menu">
              <SidebarMenuSkeleton count={8} hasSubMenu={true} />
            </ul>
          ) : (
            <ul className="v2-sidebar__menu">
              {allMenus.map((menuGroup, menuIndex) => {
                const hasSubMenu = menuGroup.sub_menu?.length > 0;
                const isExpanded = openSubMenus[menuGroup.menu_id];
                const isActive = isMenuActive(menuGroup);

                return (
                  <li key={`menu-${menuGroup.menu_id}-${menuIndex}`} className="v2-sidebar__menu-item">
                    {hasSubMenu ? (
                      <>
                        <button
                          className={`v2-sidebar__menu-link ${isActive ? "v2-sidebar__menu-link--active" : ""}`}
                          onClick={() => handleToggleMenu(menuGroup.menu_id)}
                        >
                          <span className="v2-sidebar__menu-icon">
                            {getMenuIcon(menuGroup.label)}
                          </span>
                          <span className="v2-sidebar__menu-text">
                            {menuGroup.label}
                          </span>
                          <span className={`v2-sidebar__menu-arrow ${isExpanded ? "v2-sidebar__menu-arrow--open" : ""}`}>
                            <ChevronDownIcon />
                          </span>
                          {!isOpen && (
                            <span className="v2-sidebar__tooltip">
                              {menuGroup.label}
                            </span>
                          )}
                        </button>
                        <ul className={`v2-sidebar__submenu ${isExpanded ? "v2-sidebar__submenu--open" : ""}`}>
                          {menuGroup.sub_menu.map((subItem, subIndex) => (
                            <li key={`submenu-${subItem.feature_id}-${subIndex}`} className="v2-sidebar__submenu-item">
                              <Link
                                to={subItem.path}
                                className={`v2-sidebar__submenu-link ${isActiveRoute(subItem.path) ? "v2-sidebar__submenu-link--active" : ""}`}
                                onClick={handleSubMenuClick}
                              >
                                {subItem.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <Link
                        to={menuGroup.path}
                        className={`v2-sidebar__menu-link ${isActive ? "v2-sidebar__menu-link--active" : ""}`}
                        onClick={handleMenuClick}
                      >
                        <span className="v2-sidebar__menu-icon">
                          {getMenuIcon(menuGroup.label)}
                        </span>
                        <span className="v2-sidebar__menu-text">
                          {menuGroup.label}
                        </span>
                        {!isOpen && (
                          <span className="v2-sidebar__tooltip">
                            {menuGroup.label}
                          </span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        <div className="v2-sidebar__footer">
          <div className="v2-sidebar__footer-item">
            <Link to="/settings" className="v2-sidebar__settings-link" onClick={handleSubMenuClick}>
              <SettingsIcon />
              {isOpen && <span>Settings</span>}
              {!isOpen && <span className="v2-sidebar__tooltip">Settings</span>}
            </Link>
          </div>
          <div className="v2-sidebar__footer-item">
            <button className="v2-sidebar__logout-btn" onClick={handleLogout}>
              <LogoutIcon />
              {isOpen && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SideBarContentV2;
